import { useEffect, useRef } from 'react';
import { sections, sectionCount } from '@/data/sections';
import { useActiveSection } from '@/hooks/useActiveSection';
import { useKathmanduClock } from '@/hooks/useKathmanduClock';

const IDS = sections.map((s) => s.id);

/**
 * The HUD rail — v9's persistent telemetry chrome (docs/DESIGN-V9.md §6 #5).
 *
 * Mauricio Juba's portfolio proved the grammar: live readouts as *structure*,
 * not garnish. Three instruments, all real:
 * - left edge: where you are in the eight-part document + SCRL percent
 * - bottom-right: Asia/Kathmandu wall clock (UTC+05:45) + cursor position
 *
 * Scroll and pointer values are written straight to DOM nodes through refs
 * inside a single rAF loop — no React state, no re-render per scroll event,
 * per the transform/opacity-only motion law. `aria-hidden` throughout: the
 * nav already exposes section position accessibly, and a screen reader
 * announcing coordinates on every mousemove is noise, not information.
 */
export function HudRail() {
  const activeId = useActiveSection(IDS);
  const active = sections.find((s) => s.id === activeId) ?? sections[0]!;
  const time = useKathmanduClock();
  const scrlRef = useRef<HTMLSpanElement>(null);
  const crsrRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let raf = 0;
    let scrollY = window.scrollY;
    let pointer: { x: number; y: number } | null = null;

    const write = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? Math.round((scrollY / max) * 100) : 0;
      if (scrlRef.current) scrlRef.current.textContent = String(pct).padStart(3, '0');
      if (crsrRef.current) {
        crsrRef.current.textContent = pointer
          ? `${String(Math.round(pointer.x)).padStart(4, '0')}·${String(Math.round(pointer.y)).padStart(4, '0')}`
          : '————·————';
      }
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(write);
    };

    const onScroll = () => {
      scrollY = window.scrollY;
      schedule();
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      pointer = { x: e.clientX, y: e.clientY };
      schedule();
    };

    write();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      {/* Left edge — provenance: "04 — RESEARCH / 08". Vertical only: the
          hero's display lines start at the gutter, and anything horizontal
          here collides with their descender rows. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed top-1/2 left-0 z-40 hidden -translate-y-1/2 lg:block"
      >
        <p className="label flex items-center gap-3 py-4 pl-2 [writing-mode:vertical-rl] [text-orientation:mixed]">
          <span className="text-acid tabular-nums">{active.number}</span>
          <span className="bg-edge h-6 w-px" />
          <span className="text-bone">{active.label}</span>
          <span className="bg-edge h-6 w-px" />
          <span className="tabular-nums">{sectionCount}</span>
        </p>
      </div>

      {/* Bottom-right — instrument cluster: cursor, scroll percent, place. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed right-(--gutter) bottom-3 z-40 hidden items-center gap-5 lg:flex"
      >
        <span ref={crsrRef} className="label tabular-nums">
          ————·————
        </span>
        <span className="bg-edge h-3 w-px" />
        <span className="label tabular-nums">
          SCRL <span ref={scrlRef}>000</span>%
        </span>
        <span className="bg-edge h-3 w-px" />
        <span className="label tabular-nums">KTM {time}</span>
      </div>
    </>
  );
}
